"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormLabel,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { OrdersQueryParams } from "@/interfaces";
import { ComboSearch } from "@/components/ComboSearch";
import MultiTagSelector from "@/components/MultiTagSelector";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Filter, X } from "lucide-react";

interface FilterOptionsProps {
  onFilterChange: (filters: Partial<OrdersQueryParams>) => void;
}

const filterSchema = z.object({
  customerId: z.string().optional(),
  tags: z.array(z.string()),
  minTotalAmount: z.string().optional(),
  maxTotalAmount: z.string().optional(),
});

export function FilterDelinquency({ onFilterChange }: FilterOptionsProps) {
  const form = useForm<z.infer<typeof filterSchema>>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      tags: [],
      customerId: "",
      minTotalAmount: "",
      maxTotalAmount: "",
    },
  });

  const onSubmit = (data: z.infer<typeof filterSchema>) => {
    const filters: OrdersQueryParams = {
      tags: data.tags,
      customerId: data.customerId,
    };

    if (data.minTotalAmount) {
      filters.minTotalAmount = Number.parseFloat(data.minTotalAmount);
    }

    if (data.maxTotalAmount) {
      filters.maxTotalAmount = Number.parseFloat(data.maxTotalAmount);
    }

    onFilterChange(filters);
  };

  const clearFilters = () => {
    form.reset();
    onFilterChange({
      tags: [],
      customerId: "",
    });
  };

  return (
    <Card className="w-full md:w-64">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={clearFilters}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Limpar filtros</span>
          </Button>
        </div>
        <Separator />
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="customerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cliente</FormLabel>
                  <FormControl>
                    <ComboSearch
                      id="customer"
                      context="customer"
                      value={field.value || ""}
                      onSelect={(value) => {
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <FormControl>
                    <MultiTagSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="minTotalAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor Mínimo</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          R$
                        </span>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          className="pl-8"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxTotalAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor Máximo</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          R$
                        </span>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          className="pl-8"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full">
              Aplicar Filtros
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}



